import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { get, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { dateOnly, humanize } from '@/lib/utils';
import { DataTable, FilterField, PageHeader, SearchFilterBar } from '@/components/ui/layout';
import { Badge, Button, Select, StatusBadge } from '@/components/ui/primitives';
import type { AdmissionRequest } from './AdmitPage';

interface AdmissionRow {
  id: string;
  number: string;
  status: string;
  source: string;
  admittedAt: string;
  dischargedAt: string | null;
  provisionalDiagnosis: string | null;
  patient: { id: string; mrn: string; fullName: string; sex: string; phone: string | null };
  doctor: { id: string; name: string };
  bed: { id: string; code: string; bedClass: string; room: { code: string; ward: { id: string; name: string } } } | null;
  payer: { id: string; name: string } | null;
}

interface AdmissionPage {
  data: AdmissionRow[];
  nextCursor: string | null;
}

const ADMISSIONS_PAGE_SIZE = 100;

function AdmissionRequests({ onAdmit }: { onAdmit: (request: AdmissionRequest) => void }) {
  const requests = useQuery({
    queryKey: ['admission-requests'],
    queryFn: () => get<{ data: AdmissionRequest[]; nextCursor: string | null }>('/ipd/admission-requests'),
  });
  if (!requests.data?.data.length) return null;
  return (
    <div className="mb-4 space-y-2">
      <p className="text-sm font-medium">{tr('ipd', 'requests_from_clinics')}</p>
      <DataTable
        rows={requests.data.data}
        loading={requests.isLoading} refreshing={requests.isFetching}
        error={requests.error}
        rowKey={(row) => row.id}
        empty={tr('ipd', 'no_requests')}
        columns={[
          { header: tr('ipd', 'asked'), sort: (row) => row.orderedAt, cell: (row) => dateOnly(row.orderedAt) },
          {
            header: tr('ipd', 'patient'),
            sort: (row) => row.patient.fullName,
            cell: (row) => (
              <div>
                <p className="font-medium">{row.patient.fullName}</p>
                <p className="text-xs text-muted-foreground">{row.patient.mrn}</p>
              </div>
            ),
          },
          { header: tr('ipd', 'urgency'), sort: (row) => row.urgency ?? 'routine', cell: (row) => <Badge variant={row.urgency === 'routine' ? 'secondary' : 'destructive'}>{humanize(row.urgency ?? 'routine')}</Badge> },
          { header: tr('ipd', 'ward'), sort: (row) => row.wardClass, cell: (row) => (row.wardClass ? humanize(row.wardClass, 'bed_class') : '—') },
          { header: tr('ipd', 'reason'), cell: (row) => <span className="line-clamp-2 max-w-xs text-sm">{row.reason ?? '—'}</span> },
          {
            header: '',
            align: 'right',
            cell: (row) => (
              <Button size="sm" onClick={() => onAdmit(row)}>
                {tr('ipd', 'admit')}
              </Button>
            ),
          },
        ]}
      />
    </div>
  );
}

/** Admissions list and the admission desk (SRS 6.6). */
export function AdmissionsPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('admitted');
  const [search, setSearch] = useState('');

  const admissions = useInfiniteQuery({
    queryKey: ['admissions', status, search],
    initialPageParam: '',
    queryFn: ({ pageParam }) =>
      get<AdmissionPage>(
        `/ipd/admissions${qs({ status, q: search || undefined, limit: ADMISSIONS_PAGE_SIZE, cursor: pageParam })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = admissions.data?.pages.flatMap((page) => page.data);

  return (
    <>
      <PageHeader
        title={tr('ipd', 'admissions')}
        description={tr('ipd', 'current_in_patients_and_their_consultants')}
        actions={can('admission.manage') && <Button onClick={() => navigate('/ipd/admissions/new')}>{tr('ipd', 'admit_patient')}</Button>}
      />

      {can('admission.manage') && (
        <AdmissionRequests onAdmit={(request) => navigate(`/ipd/admissions/new?requestId=${request.id}`)} />
      )}

      <SearchFilterBar
        search={{ value: search, onChange: setSearch, placeholder: tr('ipd', 'admission_number_patient_name_or_mrn') }}
        filters={
          <FilterField label={tr('ipd', 'status')}>
            <Select className="w-44" value={status} onChange={(e) => setStatus(e.target.value)}>
              {['admitted', 'discharged', 'lama', 'referred', 'expired', 'absconded'].map((s) => (
                <option key={s} value={s}>
                  {humanize(s)}
                </option>
              ))}
            </Select>
          </FilterField>
        }
        onReset={
          status !== 'admitted' || search
            ? () => {
                setStatus('admitted');
                setSearch('');
              }
            : undefined
        }
      />

      <DataTable
        rows={rows}
        loading={admissions.isLoading} refreshing={admissions.isFetching}
        error={admissions.error}
        rowKey={(row) => row.id}
        onRowClick={(row) => navigate(`/ipd/admissions/${row.id}`)}
        empty={tr('ipd', 'no_admissions_match')}
        columns={[
          { header: tr('ipd', 'admission'), sort: (row) => row.number, cell: (row) => <span className="tabular font-medium">{row.number}</span> },
          {
            header: tr('ipd', 'patient'),
            sort: (row) => row.patient.fullName,
            cell: (row) => (
              <div>
                <p className="font-medium">{row.patient.fullName}</p>
                <p className="text-xs text-muted-foreground">{row.patient.mrn}</p>
              </div>
            ),
          },
          {
            header: tr('ipd', 'bed'),
            sort: (row) => row.bed?.room.ward.name,
            cell: (row) =>
              row.bed ? (
                <div>
                  <p>{row.bed.room.ward.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {row.bed.code} · {humanize(row.bed.bedClass, 'bed_class')}
                  </p>
                </div>
              ) : (
                <span className="text-muted-foreground">—</span>
              ),
          },
          { header: tr('ipd', 'consultant'), sort: (row) => row.doctor.name, cell: (row) => row.doctor.name },
          { header: tr('ipd', 'source'), sort: (row) => row.source, cell: (row) => <Badge variant="secondary">{humanize(row.source)}</Badge> },
          { header: tr('ipd', 'admitted'), sort: (row) => row.admittedAt, cell: (row) => dateOnly(row.admittedAt) },
          { header: tr('ipd', 'status'), sort: (row) => row.status, cell: (row) => <StatusBadge status={row.status} /> },
        ]}
        footer={
          admissions.hasNextPage && (
            <Button
              variant="outline"
              size="sm"
              loading={admissions.isFetchingNextPage}
              onClick={() => void admissions.fetchNextPage()}
            >
              {tr('ipd', 'load_more')}
            </Button>
          )
        }
      />
    </>
  );
}

